/* eslint-disable @typescript-eslint/no-unused-vars */ /**
 * https://github.com/tldraw/tldraw/blob/550b88be8f24979ea525215ba73d22517aee1cfe/packages/tlschema/src/records/TLPageState.ts#L1
 */ import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { idValidator } from "../misc/id-validator";
import { nodeIdValidator } from "../nodes/SWBaseNode";
import { shapeIdValidator } from "../shapes/TLBaseShape";
import { SWINSTANCE_ID } from "./SWInstance";
import { CameraRecordType } from "./TLCamera";
const pageIdValidator = idValidator("page");
/** @internal */ export const instancePageStateValidator = T.model("instance_page_state", T.object({
    typeName: T.literal("instance_page_state"),
    id: idValidator("instance_page_state"),
    pageId: pageIdValidator,
    selectedShapeIds: T.arrayOf(shapeIdValidator),
    hintingShapeIds: T.arrayOf(shapeIdValidator),
    erasingShapeIds: T.arrayOf(shapeIdValidator),
    hoveredShapeId: shapeIdValidator.nullable(),
    editingShapeId: shapeIdValidator.nullable(),
    croppingShapeId: shapeIdValidator.nullable(),
    focusedGroupId: shapeIdValidator.nullable(),
    meta: T.jsonValue,
    // What I've added
    selectedNodeIds: T.arrayOf(nodeIdValidator),
    // selectedNodeRect: T.any,
    expandedNodeIds: T.arrayOf(nodeIdValidator),
    hoveredNodeId: nodeIdValidator.nullable(),
    hintedNodeId: nodeIdValidator.nullable().optional(),
    // hoveredNodeRect: T.any,
    draggingNodeId: nodeIdValidator.nullable().optional()
}));
/** @internal */ export const instancePageStateVersions = {
    AddCroppingId: 1,
    RemoveInstanceIdAndCameraId: 2,
    AddMeta: 3,
    RenameProperties: 4,
    RenamePropertiesAgain: 5,
    AddSelectedNodes: 6
};
/** @public */ export const instancePageStateMigrations = defineMigrations({
    currentVersion: instancePageStateVersions.AddSelectedNodes,
    migrators: {
        [instancePageStateVersions.AddCroppingId]: {
            up (instance) {
                return {
                    ...instance,
                    croppingShapeId: null
                };
            },
            down ({ croppingShapeId: _croppingShapeId, ...instance }) {
                return instance;
            }
        },
        [instancePageStateVersions.RemoveInstanceIdAndCameraId]: {
            up ({ instanceId: _, cameraId: __, ...instance }) {
                return instance;
            },
            down (instance) {
                // this should never be called since we bump the schema version
                return {
                    ...instance,
                    instanceId: SWINSTANCE_ID,
                    cameraId: CameraRecordType.createId("void")
                };
            }
        },
        [instancePageStateVersions.AddMeta]: {
            up: (record)=>{
                return {
                    ...record,
                    meta: {}
                };
            },
            down: ({ meta: _, ...record })=>{
                return {
                    ...record
                };
            }
        },
        [instancePageStateVersions.RenameProperties]: {
            // this migration is cursed: it was written wrong and doesn't do anything.
            // rather than replace it, I've added another migration below that fixes it.
            up: (record)=>{
                const { selectedShapeIds, hintingShapeIds, erasingShapeIds, hoveredShapeId, editingShapeId, croppingShapeId, focusedGroupId, ...rest } = record;
                return {
                    selectedShapeIds: selectedShapeIds,
                    hintingShapeIds: hintingShapeIds,
                    erasingShapeIds: erasingShapeIds,
                    hoveredShapeId: hoveredShapeId,
                    editingShapeId: editingShapeId,
                    croppingShapeId: croppingShapeId,
                    focusedGroupId: focusedGroupId,
                    ...rest
                };
            },
            down: (record)=>{
                const { selectedShapeIds, hintingShapeIds, erasingShapeIds, hoveredShapeId, editingShapeId, croppingShapeId, focusedGroupId, ...rest } = record;
                return {
                    selectedShapeIds: selectedShapeIds,
                    hintingShapeIds: hintingShapeIds,
                    erasingShapeIds: erasingShapeIds,
                    hoveredShapeId: hoveredShapeId,
                    editingShapeId: editingShapeId,
                    croppingShapeId: croppingShapeId,
                    focusedGroupId: focusedGroupId,
                    ...rest
                };
            }
        },
        [instancePageStateVersions.RenamePropertiesAgain]: {
            up: (record)=>{
                const { selectedIds, hintingIds, erasingIds, hoveredId, editingId, croppingId, focusLayerId, ...rest } = record;
                return {
                    ...rest,
                    selectedShapeIds: selectedIds,
                    hintingShapeIds: hintingIds,
                    erasingShapeIds: erasingIds,
                    hoveredShapeId: hoveredId,
                    editingShapeId: editingId,
                    croppingShapeId: croppingId,
                    focusedGroupId: focusLayerId
                };
            },
            down: (record)=>{
                const { selectedShapeIds, hintingShapeIds, erasingShapeIds, hoveredShapeId, editingShapeId, croppingShapeId, focusedGroupId, ...rest } = record;
                return {
                    ...rest,
                    selectedIds: selectedShapeIds,
                    hintingIds: hintingShapeIds,
                    erasingIds: erasingShapeIds,
                    hoveredId: hoveredShapeId,
                    editingId: editingShapeId,
                    croppingId: croppingShapeId,
                    focusLayerId: focusedGroupId
                };
            }
        },
        [instancePageStateVersions.AddSelectedNodes]: {
            up: (record)=>{
                const { selectedShapeIds, ...rest } = record;
                return {
                    ...rest,
                    selectedNodeIds: selectedShapeIds
                };
            },
            down: (record)=>{
                const { selectedNodeIds, ...rest } = record;
                return {
                    ...rest,
                    selectedShapeIds: selectedNodeIds
                };
            }
        }
    }
});
/** @public */ export const SWInstancePageStateRecordType = createRecordType("instance_page_state", {
    migrations: instancePageStateMigrations,
    validator: instancePageStateValidator,
    scope: "session"
}).withDefaultProperties(()=>({
        editingShapeId: null,
        croppingShapeId: null,
        selectedShapeIds: [],
        hoveredShapeId: null,
        erasingShapeIds: [],
        hintingShapeIds: [],
        focusedGroupId: null,
        meta: {},
        // What I've added
        selectedNodeIds: [],
        // selectedNodeRect: null,
        expandedNodeIds: [],
        hoveredNodeId: null,
        hintedNodeId: null,
        // hoveredNodeRect: null,
        draggingNodeId: null
    }));
